import { useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useAppSelector } from "../redux/hooks";
import { LoadingIndicator } from "@uhg-harmony/react";
import { createLogger } from "../features/notifications/utils/logger";
import TemplateBuilder from "../components/TemplateBuilder";
import ErrorBoundary from "../components/ErrorBoundary";
import AppLayout from "./AppLayout";
import NotFound from "./NotFound";
import LoggedOut from "./LoggedOut";
import { NotificationData } from "../features/notifications/masthead/NotificationData";

const logger = createLogger("ApplicationRoutes");
const OIDC_CALLBACK_PATH_SEGMENTS = ["callback", "signin-oidc", "silent-renew"];

const ProtectedEditor = () => {
  const auth = useAuth();
  const location = useLocation();
  const isAuthSyncComplete = useAppSelector((state) => state.user.isAuthSyncComplete);

  const pathname = location.pathname.toLowerCase();
  const searchParams = new URLSearchParams(location.search);
  const hasOidcError = searchParams.has("error");
  const isOidcCallback =
    OIDC_CALLBACK_PATH_SEGMENTS.some((segment) => pathname.includes(segment)) ||
    searchParams.has("code") ||
    searchParams.has("state") ||
    searchParams.has("session_state") ||
    hasOidcError;

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && (!isOidcCallback || hasOidcError)) {
      auth
        .signinRedirect({ state: { returnTo: location.pathname } })
        .catch((error) => {
          logger.error("Error during signin redirect:", error);
        });
    }
  }, [auth, hasOidcError, isOidcCallback, location.pathname]);

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated && !isOidcCallback) {
      const cleanUrl = window.location.origin + "/";
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [auth.isLoading, auth.isAuthenticated, isOidcCallback]);

  if (auth.isLoading) {
    return (
      <LoadingIndicator
        loading
        loadingText="Loading..."
        centerSpinner
        displayOverlay
        accessibilityText="Loading application"
      />
    );
  }

  if (auth.error) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: "1rem" }}>
        <h2>Authentication Error</h2>
        <p>{auth.error.message || "An error occurred during sign-in. Please try again."}</p>
        <button onClick={() => auth.signinRedirect()}>Retry Sign In</button>
      </div>
    );
  }

  if (!auth.isAuthenticated && !isOidcCallback) {
    return <div>Redirecting to login...</div>;
  }

  if (isOidcCallback && !auth.isAuthenticated) {
    return <div>Processing sign-in…</div>;
  }

  if (!isAuthSyncComplete) {
    return (
      <LoadingIndicator
        loading
        loadingText="Loading..."
        centerSpinner
        displayOverlay
        accessibilityText="Loading application"
      />
    );
  }

  return (
    <ErrorBoundary>
      <TemplateBuilder />
    </ErrorBoundary>
  );
};

const ApplicationRoutes = () => (
  <Routes>
    <Route element={<AppLayout />}>
      <Route path="/" element={<ProtectedEditor />} />
      <Route path="/index.html" element={<ProtectedEditor />} />
      <Route path="/template-studio-ui" element={<ProtectedEditor />} />
      <Route path="/template-studio-ui/" element={<ProtectedEditor />} />
      <Route path="/template-studio-ui/index.html" element={<ProtectedEditor />} />
      <Route path="/notification-data" element={<NotificationData />} />
      <Route path="/logout" element={<LoggedOut />} />
      <Route path="/logged-out" element={<LoggedOut />} />
      <Route path="*" element={<NotFound />} />
    </Route>
  </Routes>
);

export default ApplicationRoutes;
